import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Stack,
  Checkbox
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import CSVUploader from './CSVUploader';

const ExpandableRow = styled(TableRow)(({ theme }) => ({
  '& > *': {
    borderBottom: 'unset',
  },
}));

const SiteRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function CompanyList({
  companies,
  selectedSiteIds,
  onCompanySelect,
  onSiteSelect,
  onEditCompany,
  onAddCsv,
  onDeleteCsv
}) {
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [expandedSites, setExpandedSites] = useState({});

  const toggleCompany = (companyId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  const toggleSite = (siteId) => {
    setExpandedSites(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  const isCompanySelected = (company) => {
    return company.sites.every(site => selectedSiteIds.includes(site.id));
  };

  const isCompanyPartiallySelected = (company) => {
    return company.sites.some(site => selectedSiteIds.includes(site.id)) && 
           !company.sites.every(site => selectedSiteIds.includes(site.id));
  };

  const handleCompanyCheckboxChange = (company) => {
    const companySiteIds = company.sites.map(site => site.id);
    const allSelected = companySiteIds.every(id => selectedSiteIds.includes(id));

    if (allSelected) {
      // If all sites are selected, deselect all sites from this company
      onCompanySelect(company.id);
    } else {
      // If not all sites are selected, select all sites from this company
      onCompanySelect(company.id);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 4 }}>Companies</Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="40px" />
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Selected Sites</TableCell>
              <TableCell width="100px">Actions</TableCell>
              <TableCell width="40px" />
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company) => (
              <React.Fragment key={company.id}>
                <ExpandableRow>
                  <TableCell>
                    <Checkbox
                      checked={isCompanySelected(company)}
                      indeterminate={isCompanyPartiallySelected(company)}
                      onChange={() => handleCompanyCheckboxChange(company)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="text"
                        onClick={() => onCompanySelect(company.id)}
                        sx={{
                          color: isCompanySelected(company) ? 'primary.main' : 
                                 isCompanyPartiallySelected(company) ? 'warning.main' : 'text.primary',
                          fontWeight: isCompanySelected(company) || isCompanyPartiallySelected(company) ? 'bold' : 'normal'
                        }}
                      >
                        {company.name}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>{company.adres}</TableCell>
                  <TableCell>{company.contactpersoon}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {company.sites.map(site => (
                        <Chip
                          key={site.id}
                          label={site.naamLocatie}
                          size="small"
                          color={selectedSiteIds.includes(site.id) ? "primary" : "default"}
                          onClick={() => onSiteSelect(site.id)}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => onEditCompany(company)}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleCompany(company.id)}
                    >
                      {expandedCompanies[company.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                </ExpandableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={expandedCompanies[company.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Sites
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width="40px" />
                              <TableCell>Name</TableCell>
                              <TableCell>EAN Code</TableCell>
                              <TableCell>Aansluitdienst</TableCell>
                              <TableCell>Levering GTV (kW)</TableCell>
                              <TableCell>Vermogen Beperkt (kW)</TableCell>
                              <TableCell>Teruglevering GTV (kW)</TableCell>
                              <TableCell>CSV Files</TableCell>
                              <TableCell width="100px">Actions</TableCell>
                              <TableCell width="40px" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {company.sites.map((site) => (
                              <React.Fragment key={site.id}>
                                <SiteRow>
                                  <TableCell>
                                    <Checkbox
                                      checked={selectedSiteIds.includes(site.id)}
                                      onChange={() => onSiteSelect(site.id)}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="text"
                                      onClick={() => onSiteSelect(site.id)}
                                      sx={{
                                        color: selectedSiteIds.includes(site.id) ? 'primary.main' : 'text.primary',
                                        fontWeight: selectedSiteIds.includes(site.id) ? 'bold' : 'normal'
                                      }}
                                    >
                                      {site.naamLocatie}
                                    </Button>
                                  </TableCell>
                                  <TableCell>{site.eanCode}</TableCell>
                                  <TableCell>{site.aansluitdienst}</TableCell>
                                  <TableCell>{site.leveringGTV}</TableCell>
                                  <TableCell>{site.vermogenBeperktTot}</TableCell>
                                  <TableCell>{site.terugleveringGTV}</TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={1}>
                                      {site.csvData.map(csv => (
                                        <Chip
                                          key={csv.id}
                                          label={csv.fileName}
                                          size="small"
                                          onDelete={() => onDeleteCsv(site.id, csv.id)}
                                        />
                                      ))}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    <CSVUploader
                                      onUpload={(file) => onAddCsv(site.id, file)}
                                      buttonProps={{
                                        size: "small",
                                        startIcon: <UploadIcon />
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleSite(site.id)}
                                    >
                                      {expandedSites[site.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </TableCell>
                                </SiteRow>
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default CompanyList; 